import { AppBar, AppBarContainer, AppBarHeading } from "~/components/AppBar";
import { Container } from "~/components/Container";
import { Flex } from "~/components/Flex";
import { Grid } from "~/components/Grid";
import { AdvanceByReason } from "./components/AdvanceByReason";
import { AdvanceByTraveler } from "./components/AdvanceByTraveler";
import { DimensionalTable } from "./components/DimensionalTable";
import { Filters } from "./components/Filters";
import { KPICards } from "./components/KPICards";
import { PolicyByProduct } from "./components/PolicyByProduct";
import { PolicyByUnit } from "./components/PolicyByUnit";
import { PolicyDonuts } from "./components/PolicyDonuts";
import { PolicyTrends } from "./components/PolicyTrends";
import { ProductChart } from "./components/ProductChart";
import { Section } from "./components/Section";
import { SpendingChart } from "./components/SpendingChart";
import { TopViolationsTable } from "./components/TopViolationsTable";
import { UnitChart } from "./components/UnitChart";
import {
  AdvanceByReasonResponse,
  AdvanceByTravelerResponse,
  ByProductResponse,
  DimensionalResponse,
  PolicyByUnitResponse,
  PolicyComplianceResponse,
  SummaryResponse,
  TopViolationsResponse,
  TotalSpentResponse,
  TravelAnalyticsPageFilters,
} from "./types";

export interface DashboardBIV2ContainerProps {
  filters: TravelAnalyticsPageFilters;
  onChangeFilters: (filters: TravelAnalyticsPageFilters) => void;
  summary?: SummaryResponse;
  isFetchingSummary: boolean;
  totalSpent?: TotalSpentResponse;
  isFetchingTotalSpent: boolean;
  byProduct?: ByProductResponse;
  isFetchingByProduct: boolean;
  byUnit?: DimensionalResponse;
  isFetchingByUnit: boolean;
  policyByUnit?: PolicyByUnitResponse;
  isFetchingPolicyByUnit: boolean;
  byCostCenter?: DimensionalResponse;
  isFetchingByCostCenter: boolean;
  byProject?: DimensionalResponse;
  isFetchingByProject: boolean;
  policyCompliance?: PolicyComplianceResponse;
  isFetchingPolicyCompliance: boolean;
  costCenterPage: number;
  onCostCenterPageChange: (page: number) => void;
  costCenterSearch: string;
  onCostCenterSearchChange: (search: string) => void;
  projectPage: number;
  onProjectPageChange: (page: number) => void;
  projectSearch: string;
  onProjectSearchChange: (search: string) => void;
  topViolationsCostCenter?: TopViolationsResponse;
  isFetchingTopViolationsCostCenter: boolean;
  topViolationsTraveler?: TopViolationsResponse;
  isFetchingTopViolationsTraveler: boolean;
  topViolationsRequester?: TopViolationsResponse;
  isFetchingTopViolationsRequester: boolean;
  topViolationsApprover?: TopViolationsResponse;
  isFetchingTopViolationsApprover: boolean;
  advanceByReason?: AdvanceByReasonResponse;
  isFetchingAdvanceByReason: boolean;
  advanceByTraveler?: AdvanceByTravelerResponse;
  isFetchingAdvanceByTraveler: boolean;
}

export const DashboardBIV2Container = ({
  filters,
  onChangeFilters,
  summary,
  isFetchingSummary,
  totalSpent,
  isFetchingTotalSpent,
  byProduct,
  isFetchingByProduct,
  byUnit,
  isFetchingByUnit,
  policyByUnit,
  isFetchingPolicyByUnit,
  byCostCenter,
  isFetchingByCostCenter,
  byProject,
  isFetchingByProject,
  policyCompliance,
  isFetchingPolicyCompliance,
  costCenterPage,
  onCostCenterPageChange,
  costCenterSearch,
  onCostCenterSearchChange,
  projectPage,
  onProjectPageChange,
  projectSearch,
  onProjectSearchChange,
  topViolationsCostCenter,
  isFetchingTopViolationsCostCenter,
  topViolationsTraveler,
  isFetchingTopViolationsTraveler,
  topViolationsRequester,
  isFetchingTopViolationsRequester,
  topViolationsApprover,
  isFetchingTopViolationsApprover,
  advanceByReason,
  isFetchingAdvanceByReason,
  advanceByTraveler,
  isFetchingAdvanceByTraveler,
}: DashboardBIV2ContainerProps) => {
  return (
    <Flex direction="column">
      <AppBar>
        <AppBarContainer>
          <AppBarHeading title="Dashboard BI" />
        </AppBarContainer>
      </AppBar>
      <Container css={{ py: "$10", "@mxlg": { p: "$5" }, "@lg": { maxWidth: "100vw" } }}>
        <Filters filters={filters} onChange={onChangeFilters} />
        <Section title="Visão Geral">
          <Flex direction="column" gap="6">
            <KPICards summary={summary} isFetching={isFetchingSummary} />
            <SpendingChart data={totalSpent} isFetching={isFetchingTotalSpent} />
          </Flex>
        </Section>
        <Section title="Gastos por Produto">
          <ProductChart data={byProduct} isFetching={isFetchingByProduct} />
        </Section>
        <Section title="Gastos por Centro de Custo">
          <DimensionalTable
            title="Gastos por Centro de Custo — mês a mês"
            searchPlaceholder="Buscar centro de custo..."
            firstColumnHeader="Centro de custo"
            emptyMessage="Nenhum centro de custo encontrado para o período."
            filters={filters}
            data={byCostCenter}
            isFetching={isFetchingByCostCenter}
            page={costCenterPage}
            onPageChange={onCostCenterPageChange}
            search={costCenterSearch}
            onSearchChange={onCostCenterSearchChange}
          />
        </Section>
        <Section title="Gastos por Projeto">
          <DimensionalTable
            title="Gastos por Projeto — mês a mês"
            searchPlaceholder="Buscar projeto..."
            firstColumnHeader="Projeto"
            emptyMessage="Nenhum projeto encontrado para o período."
            filters={filters}
            data={byProject}
            isFetching={isFetchingByProject}
            page={projectPage}
            onPageChange={onProjectPageChange}
            search={projectSearch}
            onSearchChange={onProjectSearchChange}
          />
        </Section>
          <Section title="Conformidade de Políticas">
            <Flex direction="column" gap="6">
              <PolicyDonuts summary={summary} isFetching={isFetchingSummary} />
              <PolicyTrends data={policyCompliance} isFetching={isFetchingPolicyCompliance} />
              <PolicyByProduct
                policyCompliance={policyCompliance}
                byProduct={byProduct}
                isFetching={isFetchingPolicyCompliance || isFetchingByProduct}
              />
            </Flex>
          </Section>
          <Section title="Gastos por Unidade">
            <UnitChart data={byUnit} isFetching={isFetchingByUnit} />
          </Section>
          <Section title="Aderência por Unidade">
            <PolicyByUnit data={policyByUnit} isFetching={isFetchingPolicyByUnit} />
          </Section>
          <Section title="Maiores Violações">
            <Grid
              css={{
                gap: "$6",
                gridTemplateColumns: "1fr",
                "@lg": { gridTemplateColumns: "1fr 1fr" },
              }}
            >
              <TopViolationsTable
                title="Por Centro de Custo"
                data={topViolationsCostCenter}
                isFetching={isFetchingTopViolationsCostCenter}
              />
              <TopViolationsTable
                title="Por Viajante"
                data={topViolationsTraveler}
                isFetching={isFetchingTopViolationsTraveler}
              />
              <TopViolationsTable
                title="Por Solicitante"
                data={topViolationsRequester}
                isFetching={isFetchingTopViolationsRequester}
              />
              <TopViolationsTable
                title="Por Aprovador"
                data={topViolationsApprover}
                isFetching={isFetchingTopViolationsApprover}
              />
            </Grid>
          </Section>
          <Section title="Antecedência de Compra">
            <Grid
              css={{
                gap: "$6",
                gridTemplateColumns: "1fr",
                "@lg": { gridTemplateColumns: "1fr 1fr" },
              }}
            >
              <AdvanceByReason data={advanceByReason} isFetching={isFetchingAdvanceByReason} />
              <AdvanceByTraveler data={advanceByTraveler} isFetching={isFetchingAdvanceByTraveler} />
            </Grid>
          </Section>
      </Container>
    </Flex>
  );
};

DashboardBIV2Container.displayName = "DashboardBIV2Container";
